/**
 * usePersistentState — Syncs component state to URL search params.
 *
 * Uses microtask batching so multiple `setValue` calls in the same synchronous
 * block produce a single URL update (avoids the React-Router stale-closure
 * pitfall when several hooks call setSearchParams in the same tick).
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useRef } from 'react';

/* ─── Microtask batcher ───────────────────────────────────────────────────── */

type FlushFn = (updates: Record<string, string>) => void;

let pendingBatch: Record<string, string> | null = null;
let scheduledFlush: FlushFn | null = null;

function enqueueBatch(key: string, value: string, flush: FlushFn) {
  if (!pendingBatch) {
    pendingBatch = {};
    queueMicrotask(() => {
      const batch = pendingBatch!;
      const fn = scheduledFlush!;
      pendingBatch = null;
      scheduledFlush = null;
      fn(batch);
    });
  }
  // Always keep the latest flush function (handles component re-renders)
  scheduledFlush = flush;
  pendingBatch[key] = value;
}

/* ─── Hook ────────────────────────────────────────────────────────────────── */

/**
 * Persists a single value in the URL search params.
 *
 * - When value equals `defaultValue` (or is empty), the key is removed from
 *   the URL for cleanliness.
 * - Uses `replaceState` so search/filter changes don't pollute browser history.
 * - Supports batch: calling multiple setters in the same event handler results
 *   in a single URL update.
 *
 * @example
 * const [tab, setTab] = usePersistentState<'products' | 'options'>('subtab', 'products');
 * const [search, setSearch] = usePersistentState('search');
 */
export function usePersistentState<T extends string = string>(
  key: string,
  defaultValue: T = '' as T,
): [T, (v: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = (searchParams.get(key) ?? defaultValue) as T;

  // Keep a stable ref to the latest setSearchParams so the microtask
  // always uses the most recent version.
  const setRef = useRef(setSearchParams);
  setRef.current = setSearchParams;

  const setValue = useCallback(
    (next: T) => {
      const urlValue = (next === defaultValue || next === '') ? '' : next;
      enqueueBatch(key, urlValue, (updates) => {
        setRef.current((prev) => {
          const sp = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === '') sp.delete(k);
            else sp.set(k, v);
          }
          return sp;
        }, { replace: true });
      });
    },
    [key, defaultValue],
  );

  return [value, setValue];
}
